import React from 'react';

import { SsoProvider, useSso } from '../providers/SsoProvider';
import type { SsoConfig, SsoInjectedProps } from '../types';

/**
 * Backward-compatible HOC that wraps an app with {@link SsoProvider}
 * and injects `logged`, `singleSignOff`, and `requestTokenRenew` props.
 */
const withSSO =
  <P extends object>(config: SsoConfig) =>
  (WrappedAppWithSSO: React.ComponentType<P & SsoInjectedProps>) => {
    const WithSSO: React.FC<P> = (props) => (
      <SsoProvider config={config}>
        <SsoInjectedApp WrappedComponent={WrappedAppWithSSO} {...props} />
      </SsoProvider>
    );

    const SsoInjectedApp: React.FC<
      P & { WrappedComponent: React.ComponentType<P & SsoInjectedProps> }
    > = ({ WrappedComponent, ...props }) => {
      const { logged, singleSignOff, requestTokenRenew } = useSso();

      return (
        <WrappedComponent
          {...(props as P)}
          logged={logged}
          singleSignOff={singleSignOff}
          requestTokenRenew={requestTokenRenew}
        />
      );
    };

    return WithSSO;
  };

export default withSSO;
